import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pruneStaleData } from "../store/folderStore";

type Project = {
  id: string;
  name: string;
  folderLabel?: string;     // optional friendly label (not used yet)
  updatedAt: string;        // ISO string
};

const LS_KEY = "projects_v1";

function genId() {
  // No external deps; stable unique-ish id
  const r = Math.random().toString(36).slice(2);
  const t = Date.now().toString(36);
  // @ts-ignore
  const uuid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${t}-${r}`;
  return uuid;
}

function fmtDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function Home() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? (JSON.parse(raw) as Project[]) : [];
    } catch {
      return [];
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(projects));
  }, [projects]);

  const filtered = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.trim().toLowerCase();
    return projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [projects, search]);

  const createProject = () => {
    const name = newName.trim();
    if (!name) return;
    const proj: Project = {
      id: genId(),
      name,
      updatedAt: new Date().toISOString(),
    };
    const next = [proj, ...projects];
    setProjects(next);
    setModalOpen(false);
    setNewName("");
    // go straight to project page
    navigate(`/project/${proj.id}`);
  };

  const openProject = (p: Project) => {
    // (optional) bump updated time for recency ordering
    setProjects((prev) =>
      prev
        .map((x) => (x.id === p.id ? { ...x, updatedAt: new Date().toISOString() } : x))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    );
    navigate(`/project/${p.id}`);
  };

  const removeProject = (id: string) => {
    if (!confirm("Delete this project? This only affects your local browser data.")) return;
    const remaining = projects.filter((p) => p.id !== id);
    setProjects(remaining);
    // The project's VDR data (the index rows are a full data-room listing) goes with it.
    pruneStaleData(remaining.map((p) => p.id));
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fdf6",
        fontFamily: "Arial",
        padding: "32px 20px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1100,
          background: "#ffffff",
          border: "1px solid #c1e2b3",
          borderRadius: 14,
          padding: 24,
          boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
        }}
      >
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 style={{ margin: 0, color: "#2f6b2f" }}>Projects</h2>
            <div style={{ color: "#4a7d4a", fontSize: 12 }}>
              Create a project, then drag &amp; drop files on its page (preview only).
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "10px 12px",
                border: "1px solid #c1e2b3",
                borderRadius: 10,
                fontSize: 13,
                width: 240,
              }}
            />
            <button
              onClick={() => setModalOpen(true)}
              style={{
                background: "#6aa84f",
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "10px 14px",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              + New Project
            </button>
          </div>
        </div>

        {/* Empty state */}
        {projects.length === 0 && (
          <div
            style={{
              marginTop: 24,
              border: "1px dashed #c1e2b3",
              background: "#f4fbf1",
              color: "#2f6b2f",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>No projects yet</div>
            <div style={{ fontSize: 12, color: "#4a7d4a" }}>
              Click <b>+ New Project</b> to get started.
            </div>
          </div>
        )}

        {/* Grid of project cards */}
        {projects.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 16,
              marginTop: 20,
            }}
          >
            {filtered.map((p) => (
              <div
                key={p.id}
                style={{
                  border: "1px solid #cfe6c7",
                  borderRadius: 12,
                  background: "#eaf7e6",
                  padding: 16,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 14, flex: 1 }}>{p.name}</div>
                </div>
                <div style={{ fontSize: 11, color: "#4a7d4a" }}>
                  Updated: {fmtDate(p.updatedAt)}
                </div>

                {p.folderLabel && (
                  <div
                    style={{
                      fontSize: 11,
                      background: "#ffffff",
                      border: "1px solid #cfe6c7",
                      borderRadius: 8,
                      padding: "6px 8px",
                      color: "#2f6b2f",
                    }}
                  >
                    Folder: {p.folderLabel}
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                  <button
                    onClick={() => openProject(p)}
                    style={{
                      flex: 1,
                      background: "#6aa84f",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Open
                  </button>
                  <button
                    onClick={() => removeProject(p.id)}
                    style={{
                      background: "#ffffff",
                      color: "#2f6b2f",
                      border: "1px solid #cfe6c7",
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                    title="Delete (local only)"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Project Modal */}
      {modalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "92%",
              maxWidth: 440,
              background: "#ffffff",
              borderRadius: 12,
              padding: 20,
              border: "1px solid #c1e2b3",
              boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontWeight: 800, color: "#2f6b2f", marginBottom: 12 }}>
              Create New Project
            </div>
            <label style={{ display: "block", fontSize: 12, color: "#2f6b2f" }}>
              Project name
            </label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Client A — FDD Q4"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "10px 12px",
                marginTop: 6,
                border: "1px solid #c1e2b3",
                borderRadius: 10,
                fontSize: 13,
              }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setNewName("");
                }}
                style={{
                  background: "#e0e0e0",
                  color: "#333",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={createProject}
                disabled={!newName.trim()}
                style={{
                  background: !newName.trim() ? "#9bbf8f" : "#6aa84f",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  cursor: !newName.trim() ? "not-allowed" : "pointer",
                  fontWeight: 700,
                }}
              >
                Create & Open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
